/**
 * An imported book's chapters (decision 40: auto-split; part 18 log, table C): where the book splits, each chapter's
 * title, and each chapter written as its stored text. Pure.
 */
import { countWords } from '../book/words.ts';
import { parseChapter } from '../parser/parse.ts';
import { ConversionError } from './problems.ts';
import type { FlagRecorder, ImportFlag } from './flags.ts';
import { isBlankLeaf, runsTitle, writeChapter } from './write.ts';
import type { DraftBlock, DraftLeaf, WrittenChapter } from './write.ts';

/** C4: the most chapters an import splits a book into; past it the book is one chapter. */
export const MAX_CHAPTERS = 1000;

/** One chapter of an imported book: its title (null: untitled), its stored text, canonical (C8), and its words. */
export interface ImportedChapter {
  readonly title: string | null;
  readonly text: string;
  /** Part 08's count, for the review (V5). */
  readonly words: number;
}

/** An imported book, before the writer reviews it. */
export interface ImportedBook {
  /** The title the file gives (C5), or null for the page to take the file's name. */
  readonly title: string | null;
  readonly subtitle: string | null;
  /** One to {@link MAX_CHAPTERS} chapters, in order. */
  readonly chapters: readonly ImportedChapter[];
  readonly flags: readonly ImportFlag[];
}

/** R2, R4: what had to give way writing a chapter, flagged at `at` (its first block; part 23a's chapter). */
export function recordWritten(flags: FlagRecorder, at: number, written: WrittenChapter): void {
  if (written.formattingKeptOut > 0) flags.add('formatting-kept-out', at, written.formattingKeptOut);
  if (written.structureLost) flags.add('structure', at);
}

/**
 * C1: the smallest level two or more headings have; else the smallest any has; else null. A level is repeated when it
 * is found again later; levels take few values (a heading's are 1 to 9), so the looks from the end stay few.
 */
export function splitLevel(levels: readonly number[]): number | null {
  const sorted = [...levels].sort((a, b) => a - b);
  const repeated = sorted.find((level, i) => sorted.lastIndexOf(level) > i);
  return repeated ?? sorted[0] ?? null;
}

/** B10: whether a block is an empty paragraph (no text, no image). */
function isBlank(block: DraftBlock): boolean {
  return block.kind === 'paragraph' && isBlankLeaf(block);
}

/** B10: `blocks` without the empty paragraphs at their start and end. */
function trimBlank(blocks: readonly DraftBlock[]): DraftBlock[] {
  // All blank, both are -1: the slice is empty.
  return blocks.slice(blocks.findIndex((block) => !isBlank(block)), blocks.findLastIndex((block) => !isBlank(block)) + 1);
}

/** A chapter before it is written: its first top-level block's index and its blocks. */
interface Group {
  readonly from: number;
  readonly blocks: readonly DraftBlock[];
}

/**
 * Table C for a converter's draft (DOCX): `blocks` split into chapters at the split level's headings, each written
 * (table R) with the notes its references name, within `limit` characters in all (table CO). Flags met on the way are
 * recorded at each chapter's first block. Returns the chapters and the chapter each top-level block went to.
 */
export function draftChapters(
  blocks: readonly DraftBlock[],
  notes: ReadonlyMap<string, readonly DraftLeaf[]>,
  flags: FlagRecorder,
  limit: number,
): { readonly chapters: ImportedChapter[]; readonly chapterOf: (block: number) => number } {
  // Each block's heading level, null for a block that is no heading.
  const levels = blocks.map((block) => (block.kind === 'heading' ? block.level : null));
  const level = splitLevel(levels.filter((each): each is number => each !== null));
  const starts = levels.flatMap((each, i) => (each !== null && each === level ? [i] : []));
  const groups = (cuts: readonly number[]): Group[] => {
    const edges = [...new Set([0, ...cuts])];
    return edges.map((from, i) => ({ from, blocks: blocks.slice(from, edges[i + 1] ?? blocks.length) }));
  };
  let parts = groups(starts);
  // C4.
  if (parts.length > MAX_CHAPTERS) {
    flags.add('not-split', null);
    parts = [{ from: 0, blocks }];
  }
  const chapters: ImportedChapter[] = [];
  const firsts: number[] = [];
  let left = limit;
  for (const part of parts) {
    const kept = trimBlank(part.blocks);
    const heading = kept[0];
    // C3: blocks before the first heading make a chapter only when they hold something.
    if (heading === undefined) continue;
    const written = writeChapter(kept, notes, left);
    left -= written.text.length;
    recordWritten(flags, part.from, written);
    chapters.push({ title: heading.kind === 'heading' ? runsTitle(heading.runs) : null, text: written.text, words: countWords(parseChapter(written.text)) });
    firsts.push(part.from);
  }
  // C7.
  if (chapters.length === 0) throw new ConversionError('empty', 'nothing to import');
  return { chapters, chapterOf: (block) => Math.max(0, firsts.findLastIndex((first) => first <= block)) };
}
